<template>
  <div class="relative flex flex-col justify-center w-full">
    <form
      class="w-full space-y-8"
      @submit.prevent="next"
      @keydown.right.prevent="next"
      @keydown.enter.prevent="next"
      @keydown.esc.prevent="$emit('close')"
    >
      <div
        @click="showTooltip = !showTooltip"
        class="flex flex-row space-x-2 text-2xl font-bold text-bright-gray dark:text-white lg:text-4xl"
      >
        <h2>{{ item.questionDisplay }}</h2>
        <span v-if="item.required" class="text-ruby">*</span>
        <span class="cursor-pointer" v-if="item.tooltip.length"
          ><InformationCircleIcon class="w-8 h-8 text-gold"
        /></span>
      </div>
      <label for="" class="text-sm text-navada">{{ item.placeholder }} </label>
      <file-pond
        name="application-documents"
        ref="pond"
        :label-idle="t('drop')"
        :allow-multiple="true"
        capture="camera"
        accepted-file-types="application/pdf"
        credits="{}"
        @init="handleFilePondInit"
      />

      <div
        v-if="item.answer && item.answer.answer"
        class="border-t-[2px] border-dashed border-[#97969c]"
      >
        <h2
          class="px-2 mt-2 text-base font-bold text-tolopea dark:text-white md:text-lg"
        >
          {{ t("input.uploadedd") }}
        </h2>
        <FileList
          :loading="false"
          @delete="deleteFile"
          v-for="(obj, i) in item.answer.answer"
          :display="cleanFile(item?.key, i)"
          :id="item.id"
          :item="obj"
          :key="obj.id"
         application="application"/>
      </div>

      <div class="flex flex-row justify-between w-full pt-4 md:w-full">
        <div class="flex flex-row space-x-4">
          <button
            type="button"
            @click="next"
            class="inline-flex items-center justify-center flex-shrink-0 min-w-0 px-8 py-2 text-sm font-bold text-center text-black rounded-full bg-gold-alt focus:outline-none"
          >
            <span v-if="!isLoading">{{ t("input.continue") }}</span>
            <span v-else class="flex flex-row space-x-4"
              ><span>{{ t("input.saving") }}</span>
              <Spinner text-color="text-black"
            /></span>
          </button>
          <button
            type="submit"
            class="inline-flex items-center justify-center px-8 py-2 space-x-1 text-sm font-bold text-white rounded-full focus:outline-none"
          >
            <span class="text-navada">press</span>
            <span class="text-black dark:text-white">{{
              t("input.enter")
            }}</span>
            <span>
              <ReturnIcon />
            </span>
          </button>
        </div>
        <button
          v-if="!item.required"
          @click="$emit('skip', item, selected)"
          class="inline-flex items-center justify-center px-8 py-2 space-x-1 text-sm font-bold text-white rounded-full focus:outline-none"
        >
          <span class="text-black dark:text-white">{{ t("input.skip") }}</span>
          <SkipIcon />
        </button>
      </div>
    </form>
    <!-- Tooltip -->
    <tooltip-modal
      :show="showTooltip && item.tooltip.length"
      :tooltip="item.tooltip"
      @close="showTooltip = !showTooltip"
    />
    <!-- End tooltip -->

    <!-- Error -->
    <error-modal
      :show="showError"
      :error="error"
      @close="
        showError = !showError;
        error = '';
      "
    />
    <!-- End Error -->
  </div>
</template>

<script>
import { InformationCircleIcon } from "@heroicons/vue/solid";
import { computed, onMounted, reactive, ref, toRefs } from "vue";
import { useI18n } from "vue-i18n";
import vueFilePond, { setOptions } from "vue-filepond";
import "filepond/dist/filepond.min.css";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import Spinner from "../Spinner";
import FileList from "./FileList";
import ReturnIcon from "../icons/ReturnIcon";
import SkipIcon from "../icons/SkipIcon";

const FilePond = vueFilePond(
  //FilePondPluginPdfPreview,
  FilePondPluginFileValidateType
);

export default {
  name: "File",
  props: {
    application: {
      type: String,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      required: true,
    },
  },
  components: {
    InformationCircleIcon,
    SkipIcon,
    ReturnIcon,
    FileList,
    Spinner,
    FilePond,
  },
  setup(props, { emit }) {
    const { t } = useI18n();

    const pond = ref(null);
    const answer = computed(() => props.item.answer);
    const selected = ref(answer.value ? answer.value.answer : []);

    const state = reactive({
      showTooltip: false,
      showError: false,
      isDeleting: false,
      error: "",
    });

    onMounted(() => {
      selected.value = answer.value ? answer.value.answer : [];
    });

    const next = () => {
      if (
        props.item.required &&
        (answer.value === null || answer.value.length === 0)
      ) {
        state.showError = true;
        state.error = t("input.required");
        return;
      }
      emit("next", props.item, selected.value, true);
    };

    const handleFilePondInit = () => {
      setOptions({
        /*allowPdfPreview: true,
                                        pdfPreviewHeight: 600,
                                        pdfComponentExtraParams: 'toolbar=0&view=fit&page=1',*/
        server: {
          process: fDocProcess,
          revert: fDocDelete,
        },
      });
    };

    const deleteFile = async (key) => {
      if (!key || key.length === 0) {
        return;
      }
      try {
        const { data } = await axios.delete("/answers/files", {
          headers: {},
          data: {
            application: props.application,
            id: key.id,
            qid: props.item.id,
            key: key.name,
            value: key.file,
          },
        });

        emit("answer", data);
      } catch (e) {}
    };

    const fDocProcess = async (
      fieldName,
      file,
      metadata,
      load,
      error,
      progress,
      abort
    ) => {
      try {
        // Get signed URL from AWS
        const { data } = await axios.post("/assets/signed");
        const headers = data.headers;
        const options = {
          headers,
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            progress(
              percentCompleted,
              progressEvent.loaded,
              progressEvent.total
            );
          },
        };

        // Upload to AWS using signed url
        await axios.put(data.signedUrl, file, options);

        const params = {
          application: props.application,
          id: props.item.id,
          key: props.item.key,
          value: data.key,
          method: "append",
        };

        // Update answer with file key
        const res = await axios.put("/answers", params);

        // Update current files
        emit("answer", res.data);

        load(data.key);
      } catch (err) {
        error(err);
      }

      // Should expose an abort method so the request can be cancelled
      return {
        abort: () => {
          // Let FilePond know the request has been cancelled
          abort();
        },
      };
    };

    const fDocDelete = async (uniqueFileId, load, error) => {
      /*await axios.delete('/applications/files', {
                                  headers: {},
                                  data: {
                                      id: item.value.id,
                                      key: uniqueFileId
                                  }
                              })*/

      load();
    };

    const cleanFile = (f, i) => {
      return `${f.replace(/_file$/gi, "")}_${i + 1}`;
    };

    return {
      t,
      cleanFile,
      deleteFile,
      pond,
      selected,
      next,
      handleFilePondInit,
      ...toRefs(state),
    };
  },
};
</script>
